/* eslint-disable brace-style */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  ConfirmDialogComponent,
  DIALOG_RETURN_TYPES,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  CompanySettings,
  GrasslandUsage,
  SiteState,
  SoilType,
  YearlySettings,
} from 'src/app/shared/models';
import { OrderPlanningService } from 'src/app/shared/services/order-planning.service';
import { getCropYear, selectSoilTypes } from 'src/app/store/selectors';

export interface CompanySettingsDialogModel extends AbstractFormDialogData {
  grasslandUsage: GrasslandUsage;
  driftReduction: number;
  yearlySettings: YearlySettings;
  defaultSoilTypeId: number;
  automaticAdvice: boolean;
}

@Component({
  selector: 'app-company-settings-dialog',
  templateUrl: './company-settings-dialog.component.html',
  styleUrls: ['./company-settings-dialog.component.scss'],
  standalone: false,
})
export class CompanySettingsDialogComponent
  extends AbstractFormDialog<{
    companySettings: CompanySettings;
  }>
  implements OnInit, OnDestroy
{
  data = inject<CompanySettingsDialogModel>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected mdDialogRef = inject<MatDialogRef<CompanySettingsDialogComponent, AbstractDialogResultData<{
    companySettings: CompanySettings;
}>>>(MatDialogRef);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly orderPlanningService = inject(OrderPlanningService);
  private readonly matDialog = inject(MatDialog);
  private readonly locale = inject(LocaleService);

  nitrogenCutsInputValidators = [
    Validators.required,
    Validators.min(0),
    Validators.pattern(/^\d+(\.\d+)?$/),
  ];
  formGroup = this.formBuilder.group({
    companySettings: this.formBuilder.group({
      grasslandUsage: [this.data.grasslandUsage],
      driftReduction: [
        this.data.driftReduction,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      yearlySettings: this.formBuilder.group({
        year: [{ value: this.data.yearlySettings?.year, disabled: true }],
        nitrogenCuts: this.formBuilder.group({
          cutsInHaPeriod1: [
            this.data.yearlySettings?.nitrogenCuts.cutsInHaPeriod1,
            this.nitrogenCutsInputValidators,
          ],
          cutsInHaPeriod2: [
            this.data.yearlySettings?.nitrogenCuts.cutsInHaPeriod2,
            this.nitrogenCutsInputValidators,
          ],
          cutsInHaPeriod3: [
            this.data.yearlySettings?.nitrogenCuts.cutsInHaPeriod3,
            this.nitrogenCutsInputValidators,
          ],
        }),
      }),
      defaultSoilTypeId: [this.data.defaultSoilTypeId],
      automaticAdvice: [this.data.automaticAdvice],
    }),
  });

  driftReductions = [75, 90, 95, 97.5, 99];
  showGrasslandUsageDropdown: boolean;

  noneSoilType: SoilType = { id: 0, name: 'Geen' };
  soilTypes: SoilType[] = [this.noneSoilType];

  grasslandUsageOptions: string[] = Object.keys(GrasslandUsage).filter(
    (g) => g !== GrasslandUsage.NotGrassland,
  );

  cropYear$ = this.store.pipe(select(getCropYear));

  private soilTypes$ = this.store.pipe(select(selectSoilTypes));
  private readonly destroy$ = new Subject<void>();

constructor() {
  super();
}
  ngOnInit(): void {
    this.showGrasslandUsageDropdown = !!this.data.grasslandUsage;
    this.soilTypes$.subscribe((soilTypes) => {
      this.soilTypes.push(...soilTypes);
    });
    this.setupAutomaticAdviceToggleListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirm(result: { companySettings: CompanySettings }) {
    result.companySettings.yearlySettings.year = this.formGroup
      .get('companySettings.yearlySettings.year')
      .getRawValue();
    super.confirm(result);
  }

  private setupAutomaticAdviceToggleListener(): void {
    const automaticAdviceControl = this.formGroup.get('companySettings.automaticAdvice');

    automaticAdviceControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((value) => value === true && !this.data.automaticAdvice),
        switchMap(() =>
          this.cropYear$.pipe(
            take(1),
            switchMap((cropYear) => this.orderPlanningService.getCustomerPreference(cropYear)),
          ),
        ),
        filter(
          (response) =>
            response?.preferences?.some(
              (preference) => preference?.preferedDeliveryPeriods?.length > 0,
            ),
        ),

        switchMap(() =>
          this.matDialog
            .open(ConfirmDialogComponent, {
              data: {
                title: this.locale.translate('automaticAdvice'),
                text: this.locale.translate('customerPreferencesExistWarning'),
                confirmButton: true,
                cancelButton: true,
                confirmButtonText: this.locale.translate('ok'),
                cancelButtonText: this.locale.translate('cancel'),
                confirmButtonColor: 'primary',
              },
            })
            .afterClosed(),
        ),
      )
      .subscribe((result) => {
        if (result?.status !== DIALOG_RETURN_TYPES.confirm) {
          automaticAdviceControl.setValue(false, { emitEvent: false });
        }
      });
  }
}
